/* eslint-disable @typescript-eslint/no-explicit-any -- the Azure Maps SDK is loaded from the CDN without typings */
import { SiteDetail } from '@/routes/map/SitesMapPage.sitePanel'
import type { MapSite } from '@/routes/map/SitesMapPage.types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type SitesMapSitePopupProps = {
  map: any
  site: MapSite
  onClose: () => void
}

const SitesMapSitePopup = ({ map, site, onClose }: SitesMapSitePopupProps) => {
  const [content] = useState(() => document.createElement('div'))
  const popupRef = useRef<any>(null)
  const detailRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!map) return
    const atlas = (window as any).atlas
    const popup = new atlas.Popup({
      content,
      closeButton: false,
      pixelOffset: [0, -40],
      fillColor: getComputedStyle(map.getMapContainer()).getPropertyValue('--component-back-color').trim() || '#ffffff',
    })
    popupRef.current = popup
    return () => {
      popup.remove()
      popupRef.current = null
    }
  }, [map, content])

  useLayoutEffect(() => {
    const popup = popupRef.current
    if (!popup) return
    popup.setOptions({ position: [site.longitude, site.latitude] })
    popup.open(map)
  }, [map, site])

  useEffect(() => {
    const detail = detailRef.current
    if (!map || !detail) return
    const container = map.getMapContainer() as HTMLElement
    const layout = container.closest('.sites-map')
    const toolbar = layout?.querySelector('.sites-map__toolbar')
    const panel = layout?.querySelector('.sites-map-panel')
    let needsVisibility = true
    let timer: ReturnType<typeof setTimeout>

    const ensureVisible = () => {
      needsVisibility = false
      const bounds = container.getBoundingClientRect()
      const left = bounds.left + 20
      const right = bounds.right - 20
      const top = Math.max(bounds.top, toolbar?.getBoundingClientRect().bottom ?? bounds.top) + 20
      const bottom = Math.min(bounds.bottom, panel?.getBoundingClientRect().top ?? bounds.bottom) - 20

      // Keep the detail scrollable even on short screens or after rotation.
      detail.style.maxWidth = `${Math.max(0, right - left)}px`
      detail.style.maxHeight = `${Math.max(0, Math.min(420, window.innerHeight / 2, bottom - top - 40))}px`
      const popupBounds = detail.getBoundingClientRect()
      const offsetX = Math.max(0, left - popupBounds.left) + Math.min(0, right - popupBounds.right)
      const offsetY = Math.max(0, top - popupBounds.top) + Math.min(0, bottom - popupBounds.bottom)
      if (Math.abs(offsetX) < 1 && Math.abs(offsetY) < 1) return

      const [center] = map.pixelsToPositions([[bounds.width / 2 - offsetX, bounds.height / 2 - offsetY]])
      map.setCamera({ center, type: 'ease', duration: 200 })
    }

    const scheduleVisibility = () => {
      if (!needsVisibility) return
      clearTimeout(timer)
      // Wait until list selection's camera animation has finished before panning.
      timer = setTimeout(ensureVisible, 100)
    }
    const observer = new ResizeObserver(() => {
      needsVisibility = true
      scheduleVisibility()
    })
    observer.observe(container)
    observer.observe(detail)
    if (toolbar) observer.observe(toolbar)
    if (panel) observer.observe(panel)
    map.events.add('move', scheduleVisibility)
    scheduleVisibility()
    return () => {
      clearTimeout(timer)
      observer.disconnect()
      map.events.remove('move', scheduleVisibility)
    }
  }, [map, site])

  useEffect(() => {
    detailRef.current?.focus({ preventScroll: true })
  }, [site.siteId])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const detail = (
    <div
      ref={detailRef}
      className="sites-map-popup"
      role="dialog"
      aria-label={`Dettaglio sede: ${site.companyName}`}
      tabIndex={-1}
    >
      <SiteDetail site={site} onClose={onClose} />
    </div>
  )

  return createPortal(detail, content)
}

export default SitesMapSitePopup
